import { useState } from 'react';
import { Layers, ChevronsUpDown, X, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { GROUP_BY_OPTIONS, MAX_GROUP_BY_LEVELS, type GroupById } from '@/lib/project-group-by-utils';

interface ProjectGroupByFilterProps {
  groupBy: GroupById[];
  onGroupByChange: (value: GroupById[]) => void;
}

export function ProjectGroupByFilter({ groupBy, onGroupByChange }: ProjectGroupByFilterProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filteredOptions = GROUP_BY_OPTIONS.filter(
    (opt) => !search || opt.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-sm',
              'hover:bg-muted/40 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
              groupBy.length > 0 && 'border-primary/50 bg-primary/5'
            )}
          >
            <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className={groupBy.length === 0 ? 'text-muted-foreground' : 'text-foreground'}>
              {groupBy.length === 0
                ? 'Group By'
                : groupBy.length === 1
                  ? GROUP_BY_OPTIONS.find((o) => o.id === groupBy[0])?.label
                  : `${groupBy.length} groupings`}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-72" align="start" side="bottom">
          {/* Search */}
          <div className="flex items-center border-b px-3 gap-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groupings..."
              className="flex-1 py-2.5 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')}>
                <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Options */}
          <div className="py-1 max-h-64 overflow-y-auto">
            {filteredOptions.map((opt) => {
              const checked = groupBy.includes(opt.id);
              const priority = groupBy.indexOf(opt.id);
              const atLimit = !checked && groupBy.length >= MAX_GROUP_BY_LEVELS;
              return (
                <label
                  key={opt.id}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 select-none',
                    atLimit
                      ? 'opacity-50 cursor-not-allowed pointer-events-none'
                      : 'cursor-pointer hover:bg-muted/60 transition-colors',
                    checked && 'bg-primary/5'
                  )}
                >
                  <Checkbox
                    checked={checked}
                    disabled={atLimit}
                    onCheckedChange={() => {
                      const next = checked
                        ? groupBy.filter((f) => f !== opt.id)
                        : [...groupBy, opt.id];
                      onGroupByChange(next);
                    }}
                    className="rounded"
                  />
                  <span className="text-sm flex-1">{opt.label}</span>
                  {checked && groupBy.length > 1 && (
                    <span className="text-xs text-muted-foreground tabular-nums w-4 text-right">
                      {priority + 1}
                    </span>
                  )}
                </label>
              );
            })}
            {filteredOptions.length === 0 && (
              <p className="text-xs text-muted-foreground px-3 py-2">No items match.</p>
            )}
          </div>

          {/* Footer */}
          {groupBy.length > 0 && (
            <div className="border-t px-3 py-2 flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {groupBy.length >= MAX_GROUP_BY_LEVELS
                  ? `Up to ${MAX_GROUP_BY_LEVELS} grouping levels. Remove one to select another.`
                  : 'Subtotals shown for financial and hour fields.'}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs shrink-0"
                onClick={() => { onGroupByChange([]); setOpen(false); }}
              >
                Clear all
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
